import React from "react";
import { motion } from "framer-motion";
import { ChevronDown, ArrowLeftRight, Play, FileText } from "lucide-react";
import { getPortalUrl } from "../../../config/domains";

// Sample waveform bar heights for realistic audio visualizations
const SAMPLE_WAVEFORM = [
  35, 60, 45, 80, 55, 90, 70, 50, 85, 65, 95, 75, 45, 85, 60, 75, 90, 65, 50, 80, 55, 70, 45, 30,
];

// Mini Audio Waveform Component
const WaveformPlayer = ({ duration = "00:05", waveBars = SAMPLE_WAVEFORM }) => {
  return (
    <div className="flex items-center gap-2 bg-amber-50/50 border border-amber-100/90 rounded-xl px-2.5 py-2">
      <div className="w-6 h-6 rounded-full bg-[#F59E0B] flex-shrink-0 flex items-center justify-center text-white shadow-sm">
        <Play className="w-3 h-3 fill-current ml-0.5" />
      </div>
      <div className="flex items-center gap-[2px] flex-1 h-6 overflow-hidden">
        {waveBars.map((height, i) => (
          <div
            key={i}
            className="w-[2px] sm:w-[2.5px] rounded-full bg-[#F59E0B]"
            style={{ height: `${height}%`, opacity: i > waveBars.length * 0.4 ? 0.35 : 0.95 }}
          />
        ))}
      </div>
      <span className="text-[10px] text-gray-500 font-mono flex-shrink-0">{duration}</span>
    </div>
  );
};

const SectionThree = () => {
  const ctaUrl = getPortalUrl("/user-dashboard/developer-tools");

  return (
    <motion.section
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
      className="w-full py-16 sm:py-20 lg:py-24 bg-[#F8FAF9]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-[#EF8702] font-bold text-xs sm:text-sm tracking-widest uppercase mb-3">
            WHAT WE OFFER
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[42px] xl:text-5xl font-black text-gray-900 tracking-tight max-w-6xl mx-auto mb-4 md:whitespace-nowrap">
            Your Tool For Seamless Multilingual Services
          </h2>
          <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Our services empower individuals, businesses, educators, and developers to seamlessly integrate and enhance communication in African languages across various platforms and fields.
          </p>
        </div>

        {/* Asymmetric Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* ══════════════════════════════════════════════════════════════
              LEFT COLUMN (col-span-5): 2 Tall Cards
             ══════════════════════════════════════════════════════════════ */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Card 1: Text-to-Text Translations (Dark Forest Green) */}
            <div className="bg-[#1E6551] rounded-[28px] p-6 sm:p-8 text-white flex flex-col justify-between shadow-lg shadow-emerald-950/5">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-6">
                  Text-to-Text Translations
                </h3>

                {/* UI Preview Area */}
                <div className="relative my-4">
                  {/* Top Input Card */}
                  <div className="bg-white rounded-2xl p-4 shadow-md w-full max-w-[280px] text-gray-900">
                    <p className="text-[11px] font-bold text-gray-700 mb-2">Translate your text</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 mb-2.5">
                      <p className="text-xs font-semibold text-gray-900">Mo fẹ́ kọ́ èdè-èdè Áfríkà</p>
                    </div>
                    <div className="flex items-center justify-between text-[11px] font-medium text-gray-600">
                      <div className="flex items-center gap-1 border border-gray-200 rounded-md px-2 py-0.5 bg-white">
                        <span>Yoruba</span>
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                      <ArrowLeftRight className="w-3 h-3 text-gray-400" />
                      <div className="flex items-center gap-1 border border-gray-200 rounded-md px-2 py-0.5 bg-white">
                        <span>English</span>
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Overlapping Bottom Output Card */}
                  <div className="bg-white rounded-2xl p-3.5 shadow-xl border border-gray-100 w-full max-w-[250px] ml-auto -mt-4 relative z-10 text-gray-900">
                    <p className="text-xs font-semibold text-gray-900">I want to learn African languages</p>
                  </div>
                </div>
              </div>

              {/* Bottom Content */}
              <div className="mt-8 pt-2">
                <p className="text-white/90 text-xs sm:text-sm leading-relaxed mb-6">
                  Convert spoken language into text or another language in real time, ensuring accurate and culturally aware communication
                </p>
                <a
                  href={ctaUrl}
                  className="bg-white text-black font-semibold text-sm px-6 py-2.5 rounded-full shadow-sm hover:bg-gray-100 transition-all inline-block w-fit"
                >
                  Get API Access
                </a>
              </div>
            </div>

            {/* Card 2: Text-to-Speech Translations (Coral Red) */}
            <div className="bg-[#EF5350] rounded-[28px] p-6 sm:p-8 text-white flex flex-col justify-between shadow-lg shadow-red-950/5">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-6">
                  Text-to-Speech Translations
                </h3>

                {/* UI Preview Area */}
                <div className="relative my-4">
                  {/* Top Input Card */}
                  <div className="bg-white rounded-2xl p-4 shadow-md w-full max-w-[280px] text-gray-900">
                    <p className="text-[11px] font-bold text-gray-700 mb-2">Translate your text</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 mb-2.5">
                      <p className="text-xs font-semibold text-gray-900">Mo fẹ́ kọ́ èdè-èdè Áfríkà</p>
                    </div>
                    <div className="flex items-center justify-between text-[11px] font-medium text-gray-600">
                      <div className="flex items-center gap-1 border border-gray-200 rounded-md px-2 py-0.5 bg-white">
                        <span>Yoruba</span>
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                      <ArrowLeftRight className="w-3 h-3 text-gray-400" />
                      <div className="flex items-center gap-1 border border-gray-200 rounded-md px-2 py-0.5 bg-white">
                        <span>English</span>
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Overlapping Bottom Audio Card */}
                  <div className="bg-white rounded-2xl p-3.5 shadow-xl border border-gray-100 w-full max-w-[260px] ml-auto -mt-4 relative z-10 text-gray-900">
                    <p className="text-[11px] font-bold text-gray-800 mb-2">Speech translated</p>
                    <WaveformPlayer duration="00:05" />
                  </div>
                </div>
              </div>

              {/* Bottom Content */}
              <div className="mt-8 pt-2">
                <p className="text-white/90 text-xs sm:text-sm leading-relaxed mb-6">
                  Translate written text into your desired local language and bring it to life with natural sounding speech.
                </p>
                <a
                  href={ctaUrl}
                  className="bg-white text-black font-semibold text-sm px-6 py-2.5 rounded-full shadow-sm hover:bg-gray-100 transition-all inline-block w-fit"
                >
                  Get API Access
                </a>
              </div>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              RIGHT COLUMN (col-span-7): Top Wide, Middle 2-Cards, Bottom Wide
             ══════════════════════════════════════════════════════════════ */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            {/* Card 3: Text-to-speech (Mustard Gold / Ochre) */}
            <div className="bg-[#B89225] rounded-[28px] p-6 sm:p-8 text-white shadow-lg shadow-amber-950/5">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                {/* Left Text Column */}
                <div className="md:col-span-6 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-3">
                      Text-to-speech
                    </h3>
                    <p className="text-white/90 text-xs sm:text-sm leading-relaxed mb-6">
                      Convert written text into natural speech in it's original language with cultural tone and sensitivity, enhancing accessibility and engagement.
                    </p>
                  </div>
                  <a
                    href={ctaUrl}
                    className="bg-white text-black font-semibold text-sm px-6 py-2.5 rounded-full shadow-sm hover:bg-gray-100 transition-all inline-block w-fit"
                  >
                    Get API Access
                  </a>
                </div>

                {/* Right UI Preview Column */}
                <div className="md:col-span-6 relative">
                  {/* Top Input Card */}
                  <div className="bg-white rounded-2xl p-4 shadow-md w-full max-w-[270px] mx-auto text-gray-900">
                    <p className="text-[11px] font-bold text-gray-700 mb-2">Input your text</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 mb-2.5">
                      <p className="text-xs font-semibold text-gray-900">How are you doing today?</p>
                    </div>
                    <div className="inline-flex items-center gap-1 border border-gray-200 rounded-md px-2 py-0.5 bg-white text-[11px] font-medium text-gray-600">
                      <span>Nigerian pidgin</span>
                      <ChevronDown className="w-3 h-3 text-gray-400" />
                    </div>
                  </div>

                  {/* Overlapping Bottom Audio Card */}
                  <div className="bg-white rounded-2xl p-3 shadow-xl border border-gray-100 w-full max-w-[250px] ml-auto -mt-4 relative z-10 text-gray-900">
                    <WaveformPlayer duration="00:05" />
                  </div>
                </div>
              </div>
            </div>

            {/* Middle Row: 2 Half-width Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Card 4: Speech-to-Text (Emerald Mint Green) */}
              <div className="bg-[#0EA271] rounded-[28px] p-6 sm:p-7 text-white flex flex-col justify-between shadow-lg shadow-emerald-950/5">
                <div>
                  {/* Top UI Preview */}
                  <div className="relative mb-6">
                    {/* Back Card: Audio player */}
                    <div className="bg-white rounded-2xl p-3 shadow-md w-full max-w-[240px] text-gray-900">
                      <WaveformPlayer duration="00:30" />
                    </div>
                    {/* Front Overlapping Card: Speech Transcribed */}
                    <div className="bg-white rounded-2xl p-3.5 shadow-xl border border-gray-100 w-full max-w-[230px] ml-auto -mt-3 relative z-10 text-gray-900">
                      <p className="text-[11px] font-bold text-gray-800 mb-1.5">Speech transcribed</p>
                      <p className="text-xs font-semibold text-gray-900 leading-snug">
                        Sisi ni jukwaa lako namba 1 linalotumia AI.
                      </p>
                    </div>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold mb-2">
                    Speech-to-Text
                  </h3>
                  <p className="text-white/90 text-xs sm:text-sm leading-relaxed mb-6">
                    Convert spoken language into text in real time, ensuring accurate and culturally aware communication
                  </p>
                </div>

                <a
                  href={ctaUrl}
                  className="bg-white text-black font-semibold text-sm px-6 py-2.5 rounded-full shadow-sm hover:bg-gray-100 transition-all inline-block w-fit"
                >
                  Get API Access
                </a>
              </div>

              {/* Card 5: Speech Translations (Sunset Orange) */}
              <div className="bg-[#F37A34] rounded-[28px] p-6 sm:p-7 text-white flex flex-col justify-between shadow-lg shadow-orange-950/5">
                <div>
                  {/* Top UI Preview */}
                  <div className="relative mb-6">
                    {/* Back Card: File metadata */}
                    <div className="bg-white rounded-2xl p-3 shadow-md w-full max-w-[240px] text-gray-900 flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-500">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-900">Theme song.mp3</p>
                        <p className="text-[10px] text-gray-500">1.52 MB · audio/mpeg</p>
                      </div>
                    </div>
                    {/* Front Overlapping Card: Speech Translated */}
                    <div className="bg-white rounded-2xl p-3.5 shadow-xl border border-gray-100 w-full max-w-[230px] ml-auto -mt-3 relative z-10 text-gray-900">
                      <p className="text-[11px] font-bold text-gray-800 mb-1.5">Speech translated</p>
                      <WaveformPlayer duration="00:05" />
                    </div>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold mb-2">
                    Speech Translations
                  </h3>
                  <p className="text-white/90 text-xs sm:text-sm leading-relaxed mb-6">
                    Translate spoken language into desired speech in your desired local language in real time, ensuring accurate.
                  </p>
                </div>

                <a
                  href={ctaUrl}
                  className="bg-white text-black font-semibold text-sm px-6 py-2.5 rounded-full shadow-sm hover:bg-gray-100 transition-all inline-block w-fit"
                >
                  Get API Access
                </a>
              </div>
            </div>

            {/* Card 6: Speech-to-text translations (Royal Periwinkle Blue) */}
            <div className="bg-[#5167CD] rounded-[28px] p-6 sm:p-8 text-white shadow-lg shadow-indigo-950/5">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                {/* Left Text Column */}
                <div className="md:col-span-6 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-3">
                      Speech-to-text translations
                    </h3>
                    <p className="text-white/90 text-xs sm:text-sm leading-relaxed mb-6">
                      Translate spoken language into written text in your desired local language, ensuring accurate and culturally aware communication
                    </p>
                  </div>
                  <a
                    href={ctaUrl}
                    className="bg-white text-black font-semibold text-sm px-6 py-2.5 rounded-full shadow-sm hover:bg-gray-100 transition-all inline-block w-fit"
                  >
                    Get API Access
                  </a>
                </div>

                {/* Right UI Preview Column */}
                <div className="md:col-span-6 relative">
                  {/* Top File Card with Language Selectors */}
                  <div className="bg-white rounded-2xl p-4 shadow-md w-full max-w-[270px] mx-auto text-gray-900">
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-500">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-900">NKENNE 002.mp3</p>
                        <p className="text-[10px] text-gray-500">1.52 MB · audio/mpeg</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-[11px] font-medium text-gray-600">
                      <div className="flex items-center gap-1 border border-gray-200 rounded-md px-2 py-0.5 bg-white">
                        <span>Yoruba</span>
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                      <ArrowLeftRight className="w-3 h-3 text-gray-400" />
                      <div className="flex items-center gap-1 border border-gray-200 rounded-md px-2 py-0.5 bg-white">
                        <span>Swahili</span>
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Overlapping Bottom Speech Transcribed Card */}
                  <div className="bg-white rounded-2xl p-3.5 shadow-xl border border-gray-100 w-full max-w-[250px] ml-auto -mt-4 relative z-10 text-gray-900">
                    <p className="text-[11px] font-bold text-gray-800 mb-1.5">Speech transcribed</p>
                    <p className="text-xs font-semibold text-gray-900 leading-snug">
                      Sisi ni jukwaa lako namba 1 linalotumia AI.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default SectionThree;
